/**
 * Business › Analytics sub-pages (cas-40e4, Ben × Daniel sync 2026-10-16 §A3–A4).
 *
 * The Overview / Product funnel chips became side-nav sub-menus. Ben: names are
 * TBD, so every label a person reads lives here and nowhere else: the rail, the
 * crumb and the "30-day rolling" heading all come from this file.
 *
 * Pure: no Nuxt imports, so the rail, the page and the tests share it.
 */

export const ANALYTICS_SECTION_KEYS = ['financial', 'funnel', 'product', 'users'] as const;
export type AnalyticsSectionKey = (typeof ANALYTICS_SECTION_KEYS)[number];

/** The sub-pages that render cards from pages/admin/analytics.vue (the funnel has its own view). */
export type AnalyticsCardSection = Exclude<AnalyticsSectionKey, 'funnel'>;

export interface AnalyticsSection {
  key: AnalyticsSectionKey;
  /** Rail sub-menu label and the last crumb. */
  label: string;
}

export const ANALYTICS_BASE_PATH = '/admin/business/analytics';

/** Rail order. Financial first: it is where Analytics lands. */
export const ANALYTICS_SECTIONS: readonly AnalyticsSection[] = [
  { key: 'financial', label: 'Financial' },
  { key: 'funnel', label: 'Product funnel' },
  { key: 'product', label: 'Product usage' },
  { key: 'users', label: 'Users' }
];

export const DEFAULT_ANALYTICS_SECTION: AnalyticsSectionKey = 'financial';

/**
 * Heading for the metrics that cannot follow the selected range (retention
 * cohorts, stickiness). Ben: "stick it in its own section … called like 30-day
 * rolling averages", so nobody reads a trailing-30-day count as today.
 */
export const ROLLING_SECTION_LABEL = '30-day rolling';
export const ROLLING_SECTION_NOTE =
  'These always look back 30 days. They ignore the range picked above.';

/**
 * Every card of the former Overview and the sub-page it now lives on: the PR's
 * inventory table, in today's top-to-bottom order. pages/admin/analytics.vue
 * renders and loads a card only on its home (`showsCard`), and a source test
 * keeps this table and the page's `showsCard('<id>')` gates one-to-one, so no
 * card can be dropped or orphaned. `rolling` marks the metrics that cannot
 * follow the selected range; they sit in the trailing "30-day rolling" section.
 */
export const ANALYTICS_CARD_HOMES = {
  milestoneStrip: { label: 'Milestone strip + verdict', section: 'product' },
  milestoneCounts: { label: 'Daily milestone counts', section: 'product' },
  startFunnelSummary: { label: 'From /start to paid', section: 'product' },
  billingPulse: {
    label: 'Financial overview: money in, recurring base, leaks, detail (Billing Pulse)',
    section: 'financial'
  },
  stripeTransactions: {
    label: 'Stripe transactions, disputes, recent charges',
    section: 'financial'
  },
  growth: { label: 'Growth (new accounts, audited posts, ideas)', section: 'users' },
  recentUsers: { label: 'Recent users + lookup', section: 'users' },
  retentionHeatmap: { label: 'Retention heatmap', section: 'users', rolling: true },
  stickiness: { label: 'Stickiness', section: 'users', rolling: true },
  publish: { label: 'Do they publish?', section: 'product' },
  errorFeed: { label: 'Live error feed', section: 'product' },
  signupAttribution: { label: 'Signup attribution', section: 'users' },
  topWorkspaces: { label: 'Top workspaces by activity', section: 'users' },
  auditPipeline: { label: 'Audit pipeline', section: 'product' },
  auditProcessingTime: { label: 'Audit processing time', section: 'product' }
} as const satisfies Record<
  string,
  { label: string; section: AnalyticsCardSection; rolling?: true }
>;

export type AnalyticsCardId = keyof typeof ANALYTICS_CARD_HOMES;

/** The detail cards under "Want to dig in?" (rolling metrics sit after it, in their own section). */
export const INVESTIGATION_CARDS: readonly AnalyticsCardId[] = [
  'publish',
  'errorFeed',
  'signupAttribution',
  'topWorkspaces',
  'auditPipeline',
  'auditProcessingTime'
];

export function analyticsSectionPath(key: AnalyticsSectionKey): string {
  return `${ANALYTICS_BASE_PATH}/${key}`;
}

export function isAnalyticsSectionKey(value: unknown): value is AnalyticsSectionKey {
  return typeof value === 'string' && (ANALYTICS_SECTION_KEYS as readonly string[]).includes(value);
}

export function analyticsSectionLabel(key: AnalyticsSectionKey): string {
  return ANALYTICS_SECTIONS.find(s => s.key === key)?.label ?? key;
}

/**
 * The sub-page a bare or legacy Analytics URL opens. `?view=funnel` (the old
 * chip, still linked by the /start funnel summary) opens Product funnel;
 * `?view=overview` or no view opens the default.
 */
export function analyticsSectionForLegacyView(view: unknown): AnalyticsSectionKey {
  return view === 'funnel' ? 'funnel' : DEFAULT_ANALYTICS_SECTION;
}
